import { ref, watch } from 'vue';
import {
	BOOKING_TYPE_FILTER,
	HEAT_MAP_OPTIONS,
	MAX_ZOOM_LEVEL,
	MIN_ZOOM_LEVEL,
	SETTINGS,
	ZOOM_SCALE_OPTIONS,
} from '../../constants';
import {
	getResourcePlanningSetting,
	getZoomToFitZoomLevel,
	saveResourcePlanningSetting,
} from '../ResourcePlanningGantt/ResourcePlanningGanttUtils';
import { type HeatMapOption, type BookingTypeFilter } from '../../types';
import { type ResourcePlanningGanttConfig } from '../ResourcePlanningGantt/ResourcePlanningGanttConfig';
import { resetTime } from '@/utils/formatting';
import { format, lastDayOfMonth, lastDayOfWeek, startOfWeek } from 'date-fns';
import { type Project } from '@/generated/graphql';

export const useResourcePlanningControls = ({
	config,
	projectId,
}: {
	config: ResourcePlanningGanttConfig;
	projectId: Project['id'];
}) => {
	const zoomLevel = ref(
		Number(
			getResourcePlanningSetting(String(projectId), SETTINGS.ZOOM_LEVEL)
		) || 0
	);

	const savedBookingTypeFilterSetting =
		Object.values(BOOKING_TYPE_FILTER).find(
			(filter) =>
				filter ===
				getResourcePlanningSetting(
					String(projectId),
					SETTINGS.BOOKING_TYPE
				)
		) ?? BOOKING_TYPE_FILTER.FIXED_AND_TENTATIVE;

	const bookingTypeFilter = ref<BookingTypeFilter>(
		savedBookingTypeFilterSetting
	);

	const zoomToLevel = (level: number) => {
		zoomLevel.value = level;
		saveResourcePlanningSetting(projectId, SETTINGS.ZOOM_LEVEL, level);
		config.gantt.ext.zoom.setLevel(level);
	};

	const zoomIn = () => {
		const newZoomLevel = zoomLevel.value + 1;
		const prevChartWidth = config.gantt.$task_data.offsetWidth;

		if (newZoomLevel > MIN_ZOOM_LEVEL) {
			return;
		}

		const prevZoomLevelType = ZOOM_SCALE_OPTIONS[zoomLevel.value];
		const newZoomLevelType = ZOOM_SCALE_OPTIONS[newZoomLevel];

		zoomToLevel(newZoomLevel);

		if (newZoomLevelType === prevZoomLevelType) {
			const newChartWidth = config.gantt.$task_data.offsetWidth;

			if (newChartWidth === prevChartWidth) {
				zoomIn();
			}
		}
	};

	const zoomOut = () => {
		const newZoomLevel = zoomLevel.value - 1;
		const prevChartWidth = config.gantt.$task_data.offsetWidth;

		if (newZoomLevel < MAX_ZOOM_LEVEL) {
			return;
		}

		const prevZoomLevelType = ZOOM_SCALE_OPTIONS[zoomLevel.value];
		const newZoomLevelType = ZOOM_SCALE_OPTIONS[newZoomLevel];

		zoomToLevel(newZoomLevel);

		if (newZoomLevelType === prevZoomLevelType) {
			const newChartWidth = config.gantt.$task_data.offsetWidth;

			if (newChartWidth === prevChartWidth) {
				zoomOut();
			}
		}
	};

	const zoomTo = (id: string) => {
		let tmpZoomLevel = ZOOM_SCALE_OPTIONS.findIndex(
			(level) => level === id
		);

		tmpZoomLevel = tmpZoomLevel === -1 ? MIN_ZOOM_LEVEL : tmpZoomLevel + 1;

		zoomToLevel(tmpZoomLevel);
	};

	const zoomToFit = () => {
		const zoomLevel = getZoomToFitZoomLevel(config);

		if (zoomLevel) {
			zoomToLevel(zoomLevel);
		}
	};

	const getBookingStartAndEndDateByZoomLevel = (date: Date) => {
		let startDate = resetTime(date);
		let endDate = resetTime(date);
		const zoomConfig = config.getScaleConfigByZoomLevel(
			config.getZoomLevelSetting()
		);

		switch (zoomConfig?.unit) {
			case 'month':
				startDate = resetTime(format(date, 'yyyy-MM-01'));
				endDate = lastDayOfMonth(date);
				break;
			case 'week':
				const weekStartsOn = config.weekStartsOn.value;

				startDate = startOfWeek(date, { weekStartsOn });
				endDate = lastDayOfWeek(date, { weekStartsOn });
				break;
		}

		return { startDate, endDate };
	};

	const savedHeatMapSetting =
		Object.values(HEAT_MAP_OPTIONS).find(
			(option) =>
				option ===
				getResourcePlanningSetting(String(projectId), SETTINGS.HEAT_MAP)
		) ?? HEAT_MAP_OPTIONS.PERCENT;

	const heatMapValue = ref<HeatMapOption>(savedHeatMapSetting);

	watch(heatMapValue, (newValue) => {
		saveResourcePlanningSetting(projectId, SETTINGS.HEAT_MAP, newValue);

		config.setHeatMapDisplayValue(newValue);
		config.gantt.render();
	});

	return {
		heatMapValue,
		zoomLevel,
		zoomToLevel,
		zoomIn,
		zoomOut,
		zoomTo,
		zoomToFit,
		bookingTypeFilter,
		getBookingStartAndEndDateByZoomLevel,
	};
};
